// Motore di calcolo puro per la partita IVA in regime ordinario (contabilita'
// semplificata / ordinaria, persona fisica). Nessuna dipendenza dal DOM: viene
// riusato sia dal controller di pagina (js/pivaOrdinaria.js) sia dai test
// (tests/pivaOrdinaria.test.js).
//
// A differenza del forfettario (coefficiente di redditivita' + imposta
// sostitutiva piatta), il regime ordinario tassa il reddito effettivo
// (ricavi - costi reali - contributi) con IRPEF progressiva a scaglioni piu'
// addizionali regionale e comunale. L'IVA e' neutra (incassata dai clienti e
// versata allo Stato) quindi tutti gli importi sono al netto IVA. L'IRAP non e'
// dovuta dalle persone fisiche dal 2022.
//
// Gli import sono relativi (non "/js/...") cosi' il modulo e' eseguibile anche
// sotto node --test, oltre che bundlato da Parcel per il browser.
import {
    calculateIrpef,
    calculateInps,
    calculateAddizionaleRegionale,
    calculateAddizionaleComunale,
    calculateDetrazioniIrpef,
    calculateUlterioreDetrazione,
    calculateSommaIntegrativa
} from './employee.js';
import { calcolaContributiPrevidenziali } from './previdenza.js';

// Detrazione per redditi di lavoro autonomo/altri redditi (art. 13 c. 5 TUIR).
// Importi invariati 2025-2026 (Legge di Bilancio 2026). Si azzera oltre 50.000.
export function calcolaDetrazioneLavoroAutonomo(imponibile) {
    const reddito = Math.max(0, imponibile || 0);
    let detrazione = 0;
    if (reddito <= 5500) {
        detrazione = reddito > 0 ? 1265 : 0;
    } else if (reddito <= 28000) {
        detrazione = 500 + 765 * ((28000 - reddito) / 22500);
    } else if (reddito <= 50000) {
        detrazione = 500 * ((50000 - reddito) / 22000);
    } else {
        detrazione = 0;
    }
    // Maggiorazione di 50 EUR per redditi tra 11.000 e 17.000 EUR (c. 5-ter).
    if (reddito >= 11000 && reddito <= 17000) {
        detrazione += 50;
    }
    return Math.round(Math.max(0, detrazione));
}

// Acconti IRPEF col metodo storico: l'acconto e' pari al 100% dell'imposta.
// Soglie (art. 17 DPR 435/2001): < 51,65 EUR nessun acconto; fino a 257,52 EUR
// unica rata a novembre; oltre, due rate 40% (giugno) + 60% (novembre).
export function calcolaAcconti(imposta) {
    const base = Math.max(0, Math.round(imposta || 0));
    if (base < 51.65) {
        return { dovuto: false, modalita: 'nessuno', accontoTotale: 0, acconto1: 0, acconto2: 0 };
    }
    if (base <= 257.52) {
        return { dovuto: true, modalita: 'unica', accontoTotale: base, acconto1: 0, acconto2: base };
    }
    const acconto1 = Math.round(base * 0.40);
    const acconto2 = Math.round(base * 0.60);
    return { dovuto: true, modalita: 'rate', accontoTotale: acconto1 + acconto2, acconto1, acconto2 };
}

// Acconto contributi INPS (Gestione Separata e eccedenza Artigiani/Commercianti):
// pari all'80% dei contributi dovuti, in due rate uguali del 40% (30/6 e 30/11).
// `base` = quota di contributi soggetta a saldo + acconto (contributiAcconto).
export function calcolaAccontoInps(base) {
    const b = Math.max(0, Math.round(base || 0));
    if (b <= 0) {
        return { dovuto: false, accontoTotale: 0, rata1: 0, rata2: 0 };
    }
    const rata1 = Math.round(b * 0.40);
    const accontoTotale = Math.round(b * 0.80);
    const rata2 = Math.max(0, accontoTotale - rata1);
    return { dovuto: true, accontoTotale, rata1, rata2 };
}

// Reddito imponibile da lavoro dipendente stimato dalla RAL, usando lo stesso
// motore della pagina stipendio netto (aliquota di legge + contributo aggiuntivo
// 1% oltre la prima fascia + massimale annuo), non un 9,19% forfettario.
export function stimaRedditoDipendente(ral, data, params = {}) {
    const lordo = Math.max(0, parseFloat(ral) || 0);
    if (lordo <= 0) return 0;
    const year = params.year || '2026';
    const aliquota = data?.aliquote_inps?.lavoratore?.regolare ?? 0.0919;
    const { inpsDipendente } = calculateInps(lordo, data, {
        year,
        alinpsDip: aliquota * 100,
        alinpsDat: 0,
        mensilita: params.mensilita || 14
    });
    return Math.max(0, Math.round(lordo - inpsDipendente));
}

// Detrazione da lavoro spettante su un dato reddito complessivo.
// Art. 13 c. 5 TUIR: la detrazione per lavoro autonomo NON e' cumulabile con
// quella per lavoro dipendente (commi 1-4); chi ha entrambi i redditi fruisce
// della piu' favorevole, non della somma.
function detrazioneDaLavoro(redditoComplessivo, { conRedditoDipendente, conDetrazioneAutonomo }) {
    const daDipendente = conRedditoDipendente ? calculateDetrazioniIrpef(redditoComplessivo) : 0;
    const daAutonomo = conDetrazioneAutonomo ? calcolaDetrazioneLavoroAutonomo(redditoComplessivo) : 0;
    return Math.max(daDipendente, daAutonomo);
}

// input: {
//   ricavi, costiDeducibili,
//   gestione,                       // elemento di data/forfettario.json gestioni_previdenziali
//   mesiAttivita, nuovaAttivitaArtCom, riduzione50, ante1996, numeroMandanti,
//   fasciaEtaEnpam,                 // 'under30' | 'da30a35' | 'da35a40' | 'over40'
//   dipendenteFullTime, riduzioneDipendente, ralDipendente,
//   contabilitaOrdinaria,           // impresa in contabilita' ordinaria per opzione
//   regione,                        // chiave di data.aliquote_regionali[year]
//   comune,                         // { esenzione, scaglioni, aliquota } da getComuneSelezionato()
//   year                            // default '2026'
// }
// data: contenuto di data/data.json (aliquote_irpef, aliquote_regionali, aliquote_inps).
export function calcolaPivaOrdinaria(input, data) {
    const year = input.year || '2026';
    const ricavi = Math.max(0, parseFloat(input.ricavi) || 0);
    const costi = Math.max(0, parseFloat(input.costiDeducibili) || 0);
    const comune = input.comune || {};

    // Reddito d'impresa/professionale prima dei contributi.
    const redditoLordo = Math.max(0, ricavi - costi);
    const imponibileContributi = redditoLordo;

    const previdenza = calcolaContributiPrevidenziali(
        imponibileContributi,
        ricavi,
        input.gestione,
        {
            mesiAttivita: input.mesiAttivita,
            nuovaAttivitaArtCom: input.nuovaAttivitaArtCom,
            riduzione50: input.riduzione50,
            ante1996: input.ante1996,
            numeroMandanti: input.numeroMandanti,
            fasciaEtaEnpam: input.fasciaEtaEnpam,
            dipendenteFullTime: input.dipendenteFullTime,
            riduzioneDipendente: input.riduzioneDipendente
        }
    );
    const {
        contributi, contributiDeducibili, contributiAcconto, contributiFissi,
        contributoIntegrativo, integrativoRivalsa, contributiACarico
    } = previdenza;

    // Base imponibile IRPEF della P.IVA: reddito al netto dei contributi deducibili.
    const imponibileIrpef = Math.max(0, imponibileContributi - contributiDeducibili);

    // La detrazione art. 13 c. 5 TUIR copre i redditi di lavoro autonomo (art. 53)
    // e i redditi d'impresa "minori" (art. 66, contabilita' semplificata). Chi
    // opta per la contabilita' ordinaria non ne ha diritto.
    const conDetrazioneAutonomo = !input.contabilitaOrdinaria;

    // Se si e' gia' lavoratori dipendenti/pensionati, il reddito P.IVA si somma
    // al reddito complessivo: l'IRPEF sulla quota P.IVA e' la differenza tra
    // l'imposta netta sul complessivo e quella sul solo stipendio. Cosi' si
    // cattura non solo lo scaglione marginale ma anche l'erosione delle
    // detrazioni da lavoro dipendente, che e' la parte piu' pesante.
    const redditoDipendente = input.dipendenteFullTime
        ? stimaRedditoDipendente(input.ralDipendente, data, { year })
        : 0;
    const imponibileTotale = redditoDipendente + imponibileIrpef;

    const paramsReg = { regione: input.regione || '', year };
    const paramsCom = {
        esenzione: comune.esenzione,
        scaglioni: comune.scaglioni,
        addizionaleComunale: comune.aliquota
    };

    let irpefLorda;
    let detrazioni;
    let irpefNetta;
    let addizionaleRegionale;
    let addizionaleComunale;
    let sommaIntegrativaPersa = 0;

    if (redditoDipendente > 0) {
        // --- Imposta netta sul solo stipendio (situazione di partenza) --------
        const irpefLordaDip = calculateIrpef(redditoDipendente, data, { year });
        const detrDipSolo = calculateDetrazioniIrpef(redditoDipendente);
        const ulterioreDipSolo = calculateUlterioreDetrazione(redditoDipendente, 0, { cuneoFiscale: true });
        const irpefNettaDip = Math.max(0, irpefLordaDip - detrDipSolo - ulterioreDipSolo);

        // --- Imposta netta sul reddito complessivo (stipendio + P.IVA) --------
        const irpefLordaTot = calculateIrpef(imponibileTotale, data, { year });
        const detrLavoroTot = detrazioneDaLavoro(imponibileTotale, {
            conRedditoDipendente: true,
            conDetrazioneAutonomo
        });
        const ulterioreTot = calculateUlterioreDetrazione(imponibileTotale, 0, { cuneoFiscale: true });
        const irpefNettaTot = Math.max(0, irpefLordaTot - detrLavoroTot - ulterioreTot);

        // Quota IRPEF imputabile alla P.IVA.
        irpefLorda = Math.max(0, irpefLordaTot - irpefLordaDip);
        irpefNetta = Math.max(0, irpefNettaTot - irpefNettaDip);
        // Variazione delle detrazioni: negativa quando il reddito P.IVA erode le
        // detrazioni gia' godute sullo stipendio. Derivata dai due estremi cosi'
        // il dettaglio "lorda - detrazioni = netta" torna sempre.
        detrazioni = irpefLorda - irpefNetta;

        // Trattamento integrativo (art. 1 c. 4 L. 207/2024): spetta se il reddito
        // COMPLESSIVO non supera 20.000. Il reddito P.IVA puo' farlo perdere: non
        // e' un'imposta, quindi resta fuori da imposteTotali e viene esposto a parte.
        const sommaIntegrativaDip = calculateSommaIntegrativa(redditoDipendente, { cuneoFiscale: true });
        const sommaIntegrativaTot = imponibileTotale <= 20000 ? sommaIntegrativaDip : 0;
        sommaIntegrativaPersa = Math.max(0, sommaIntegrativaDip - sommaIntegrativaTot);

        // Addizionali: differenza tra quelle sul complessivo e quelle sul solo
        // stipendio. Sono dovute se l'IRPEF netta e' dovuta, quindi il gate e'
        // l'imposta NETTA (non la lorda).
        addizionaleRegionale = Math.max(0,
            calculateAddizionaleRegionale(imponibileTotale, data, irpefNettaTot, paramsReg) -
            calculateAddizionaleRegionale(redditoDipendente, data, irpefNettaDip, paramsReg));
        addizionaleComunale = Math.max(0,
            calculateAddizionaleComunale(imponibileTotale, irpefNettaTot, paramsCom) -
            calculateAddizionaleComunale(redditoDipendente, irpefNettaDip, paramsCom));
    } else {
        // Solo reddito P.IVA: IRPEF dal primo scaglione.
        irpefLorda = calculateIrpef(imponibileIrpef, data, { year });
        detrazioni = detrazioneDaLavoro(imponibileIrpef, {
            conRedditoDipendente: false,
            conDetrazioneAutonomo
        });
        irpefNetta = Math.max(0, irpefLorda - detrazioni);
        addizionaleRegionale = calculateAddizionaleRegionale(imponibileIrpef, data, irpefNetta, paramsReg);
        addizionaleComunale = calculateAddizionaleComunale(imponibileIrpef, irpefNetta, paramsCom);
    }

    const imposteTotali = irpefNetta + addizionaleRegionale + addizionaleComunale;

    // Dal netto si sottraggono i contributi effettivamente a carico: la quota
    // integrativa ribaltata in fattura al cliente entra ed esce, non e' un costo.
    // Il netto puo' essere negativo (perdita reale quando costi + contributi
    // superano i ricavi): si mostra la perdita, non si clampa a zero.
    const nettoAnnuo = Math.round(ricavi - costi - contributiACarico - imposteTotali);
    const nettoMensile = Math.round(nettoAnnuo / 12);

    // Acconti col metodo storico, distinti per tributo:
    //  - IRPEF: 100% (40% giugno + 60% novembre);
    //  - INPS: 80% (40% + 40%) sulla quota soggetta ad acconto;
    //  - addizionale comunale: 30% (regionale: nessun acconto).
    const accontoIrpef = calcolaAcconti(irpefNetta);
    const accontoInps = calcolaAccontoInps(contributiAcconto);
    const accontoComunale = accontoIrpef.dovuto ? Math.round(addizionaleComunale * 0.30) : 0;
    const acconti = { irpef: accontoIrpef, inps: accontoInps, comunale: accontoComunale };

    return {
        ricavi,
        costi,
        redditoLordo,
        imponibileContributi,
        contributi: Math.round(contributi),
        contributiDeducibili: Math.round(contributiDeducibili),
        contributoIntegrativo: Math.round(contributoIntegrativo),
        integrativoRivalsa: Math.round(integrativoRivalsa),
        contributiACarico: Math.round(contributiACarico),
        contributiAcconto: Math.round(contributiAcconto),
        contributiFissi: Math.round(contributiFissi),
        imponibilePrevidenziale: Math.round(previdenza.imponibilePrevidenziale),
        massimaleContributivo: previdenza.massimale,
        massimaleApplicato: previdenza.massimaleApplicato,
        redditoDipendente: Math.round(redditoDipendente),
        imponibileTotale: Math.round(imponibileTotale),
        imponibileIrpef: Math.round(imponibileIrpef),
        irpefLorda,
        // `detrazioni` e' la detrazione spettante nel caso standalone e la
        // VARIAZIONE di detrazioni (tipicamente negativa) nel caso misto.
        detrazioni,
        detrazioneLavoroAutonomo: detrazioni,
        sommaIntegrativaPersa,
        irpefNetta,
        addizionaleRegionale,
        addizionaleComunale,
        imposteTotali: Math.round(imposteTotali),
        nettoAnnuo,
        nettoMensile,
        acconti
    };
}
